import { useEffect, useState } from 'react';
import useCloudStore from '../store/cloudStore';
import useGameStore from '../store/gameStore';
import { money } from '../lib/format';
import { useT } from '../i18n';

interface SaveManagerModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Save Manager: manual named cloud-save slots (users/{uid}/games/{saveId}).
 * Two tabs: save the live match under a custom name, or resume / delete slots.
 */
export const SaveManagerModal = ({ open, onClose }: SaveManagerModalProps) => {
  const t = useT();
  const {
    user,
    configured,
    saveSlots,
    saveSlotsLoading,
    status,
    signIn,
    refreshSaveSlots,
    saveNamedSlot,
    loadNamedSlot,
    deleteNamedSlot,
  } = useCloudStore();
  const live = useGameStore();
  const [tab, setTab] = useState<'SAVE' | 'LOAD'>('SAVE');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) void refreshSaveSlots();
  }, [open, user, refreshSaveSlots]);

  if (!open) return null;

  const suggested = `Kolo ${live.market.currentRound} · ${live.players.length} hráčů`;

  const doSave = async () => {
    setBusy('save');
    setNotice(null);
    try {
      await saveNamedSlot(name.trim() || suggested);
      setName('');
      setTab('LOAD');
      setNotice(t('cloud.uploaded', { round: live.market.currentRound }));
    } catch {
      setNotice(t('cloud.error', { error: 'save-failed' }));
    } finally {
      setBusy(null);
    }
  };

  const doLoad = async (saveId: string) => {
    setBusy(saveId);
    setNotice(null);
    try {
      await loadNamedSlot(saveId);
      onClose();
    } catch {
      setNotice(t('cloud.error', { error: 'load-failed' }));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async (saveId: string) => {
    setBusy(`del-${saveId}`);
    try {
      await deleteNamedSlot(saveId);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl border-2 border-slate-700 bg-slate-900 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-700/60 px-4 py-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">{t('common.saveLoad')}</p>
            <h2 className="text-lg font-black text-white">{t('common.saveLoad')}</h2>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700">
            {t('common.close')}
          </button>
        </div>
        {!user ? (
          <div className="space-y-3 p-4 text-sm text-slate-300">
            <p>{configured ? t('auth.localOnly') : t('auth.cloudOff')}</p>
            {configured && (
              <button type="button" onClick={() => void signIn()} className="min-h-[40px] w-full rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900 hover:bg-slate-100">
                {t('auth.signIn')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3 p-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => setTab('SAVE')} className={`min-h-[36px] rounded-xl px-4 text-xs font-extrabold ${tab === 'SAVE' ? 'bg-amber-400 text-slate-950' : 'border border-slate-600 bg-slate-800 text-slate-300'}`}>
                Uložit hru
              </button>
              <button type="button" onClick={() => setTab('LOAD')} className={`min-h-[36px] rounded-xl px-4 text-xs font-extrabold ${tab === 'LOAD' ? 'bg-amber-400 text-slate-950' : 'border border-slate-600 bg-slate-800 text-slate-300'}`}>
                Načíst hru
              </button>
            </div>
            {tab === 'SAVE' && (
              <div className="rounded-xl border border-slate-700/60 bg-slate-950/60 p-3">
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400" htmlFor="save-slot-name">Název slotu</label>
                <input id="save-slot-name" value={name} onChange={(event) => setName(event.target.value)} placeholder={suggested} className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400" />
                <button type="button" disabled={busy !== null || status === 'SAVING'} onClick={() => void doSave()} className="mt-2 min-h-[40px] w-full rounded-xl bg-amber-400 px-4 py-2 text-sm font-black text-slate-950 hover:bg-amber-300 disabled:opacity-40">
                  Uložit aktuální stav{busy === 'save' ? '…' : ''}
                </button>
                <p className="mt-1 text-[10px] text-slate-500">Auto-save: konec každého kola.</p>
              </div>
            )}
            {tab === 'LOAD' && (
              <div className="rounded-xl border border-slate-700/60 bg-slate-950/60 p-3">
                {saveSlotsLoading ? (
                  <p className="text-xs text-slate-400">Načítám sloty…</p>
                ) : saveSlots.length === 0 ? (
                  <p className="text-xs text-slate-400">Zatím žádné uložené sloty.</p>
                ) : (
                  <div className="grid gap-2">
                    {saveSlots.map((slot) => (
                      <div key={slot.id} className="rounded-lg border border-slate-700/60 bg-slate-900/70 px-2.5 py-2 text-[11px]">
                        <p className="flex flex-wrap items-center justify-between gap-1">
                          <span className="font-bold text-slate-100">{slot.name}</span>
                          <span className="text-slate-500">{new Date(slot.updatedAt).toLocaleString()}</span>
                        </p>
                        <p className="mt-0.5 text-slate-400">
                          Kolo {slot.summary.round} · {slot.summary.playerCount} hráčů · lídr {slot.summary.leaderName} · na tahu {slot.summary.activePlayerName} · hotovost {money(slot.summary.userCash)}
                        </p>
                        <div className="mt-1.5 flex gap-1.5">
                          <button type="button" disabled={busy !== null} onClick={() => void doLoad(slot.id)} className="min-h-[30px] flex-1 rounded-lg bg-emerald-500 px-3 py-1 text-[11px] font-extrabold text-slate-950 hover:bg-emerald-400 disabled:opacity-40">
                            {busy === slot.id ? 'Načítám…' : 'Pokračovat / Load'}
                          </button>
                          <button type="button" disabled={busy !== null} onClick={() => void doDelete(slot.id)} className="min-h-[30px] rounded-lg border border-rose-400/60 bg-rose-600/20 px-3 py-1 text-[11px] font-extrabold text-rose-200 hover:bg-rose-500/30 disabled:opacity-40">
                            {busy === `del-${slot.id}` ? '…' : 'Smazat'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {notice && <p className="text-[11px] text-slate-400">{notice}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveManagerModal;